import { postData, deleteData } from '@/services/axios-service';
import { BASE_URL } from '@/utils/constants/api-constants';
import { ILoginResponse } from '@/interfaces/login-response.interface';
import { i18n } from '@/plugins/i18n';
import { defineStore, getActivePinia, storeToRefs } from 'pinia';
import { IAuthStore, LoginForm } from '@/interfaces/auth/auth-store.interface';
import router from '@/router';
import { ProfileStore, useProfileStore } from '@/stores/profile-store';

const INITIAL_STATE: IAuthStore = {
  token: '',
  user: undefined,
};

export const useAuthStore = defineStore({
  id: 'auth',
  state: (): IAuthStore => ({
    ...INITIAL_STATE,
  }),
  getters: {
    fullName(state) {
      return state.user
        ? `${state.user?.name ?? state.user.email} ${state.user?.lastName ?? ''}`
        : 'Default User';
    },
    initialsName(state) {
      return `${state.user?.name ?? state.user?.email.substring(0, 1)} ${state.user?.lastName ?? state.user?.email.substring(1, 2)}`
        .split(' ')
        .map((name: any) => name.charAt(0).toUpperCase())
        .join('');
    },
    hasProfile(state) {
      return state.user?.name == undefined;
    },
    photo(state) {
      return state.user?.photo;
    },
    joinDate(state): { years: number; days: number } {
      if (state.user?.createdAt) {
        const currentDate = new Date();
        const joinDateObj = new Date(state.user?.createdAt);
        const diffInMs = currentDate.getTime() - joinDateObj.getTime();
        const days = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
        const years = Math.floor(days / 365);
        return { years, days };
      } else {
        return { years: 0, days: 0 };
      }
    },
  },
  actions: {
    async authLogin(form: LoginForm) {
      try {
        const profileStore: ProfileStore = useProfileStore();

        const { languageSelected } = storeToRefs(profileStore);

        const response = await postData<ILoginResponse>(`${BASE_URL.USERS}auth/login`, {
          ...form,
        });

        const { success, error, data } = response;

        if (success && data) {
          this.token = `Bearer ${data.token}`;
          this.user = data.user;
          if (data.user?.Settings) {
            languageSelected.value = data.user?.Settings?.language;
          } else {
            languageSelected.value = 'en';
          }
        } else {
          throw new Error(error);
        }

        return response;
      } catch (error) {
        this.token = '';
        this.user = undefined;
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      }
    },
    async authLogout() {
      try {
        const { error, success } = await deleteData(`${BASE_URL.USERS}auth/logout`);

        (getActivePinia() as any)._s.forEach((store: any) => store.$reset());
        localStorage.clear();
        if (!success) {
          throw new Error(error);
        }
      } catch (error) {
        if (error instanceof Error) throw error;
        throw new Error(error?.toString() ?? i18n.global.t('errors.unknownError'));
      } finally {
        this.resetStore();
      }
    },
    resetStore() {
      this.token = '';
      this.user = undefined;
      localStorage.clear();
      (getActivePinia() as any)._s.forEach((store: any) => store.$reset());
      router.push({ name: 'login' });
    },
    changeEmail(email: string) {
      this.user!.email = email as string;
    },
    changePhoto(photo: string) {
      this.user!.photo = photo as string;
    },
  },
  persist: {},
});

export type AuthStore = ReturnType<typeof useAuthStore>;
